import { useContext, useEffect, useState } from 'react';
import { useQboApi } from '../../clients/quipli-service/qbo/qbo-api-v1';
import { QBOAccount, QBOConfiguration, QBOConnectionInfo, QBOItemType } from '../../clients/quipli-service/qbo/qbo';
import { Spinner } from '../../components/spinner/spinner';
import { Button } from '../../components/form/button';
import { Typography } from '../../components/typography/typography';
import './qbo-account.scss';
import { QboItemIncomeAccount } from './qbo-item-income-account';
import { OrderType } from '../../clients/quipli-service/order/order';
import { AdminContext } from '../../context/admin-context';
import { TenantContext } from '../../context/tenant-context';
import { toast } from '../../util/toasts';
import { ConnectToQboButton } from './connect-to-qbo-button';
import { usePaymentConfigApiV1 } from '../../clients/quipli-service/payment-config/payment-config-api-v1';
import { PaymentMethodConfig } from '../../clients/quipli-service/payment-config/payment-config';

export const QboAccount = () => {
  const { configuration } = useContext(TenantContext);
  const { refresh } = useContext(AdminContext);
  const [connectionInfo, setConnectionInfo] = useState<QBOConnectionInfo>();
  const [configurations, setConfigurations] = useState<QBOConfiguration[]>();
  const [accounts, setAccounts] = useState<QBOAccount[]>();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodConfig[]>();
  const { getQboConnectionInfo, disconnect, getAuthUrl, listConfigurations, updateConfiguration, listAccounts } =
    useQboApi();
  const { searchPaymentMethods } = usePaymentConfigApiV1();
  let qboEnabled = false;

  // @hipaa-exclude-start
  if (configuration?.qboEnabled) {
    qboEnabled = true;
  }

  async function refreshConnectionInfo() {
    setConnectionInfo(undefined);
    getQboConnectionInfo()
      .then((ci) => {
        setConnectionInfo(ci);
        refresh();
      })
      .catch((e) => {
        console.error(`Error loading QuickBooks connection info`, e);
        toast(`Error loading QuickBooks connection info`, 'error');
      });
  }

  useEffect(() => {
    if (qboEnabled) {
      refreshConnectionInfo();
      listConfigurations().then(setConfigurations);
      listAccounts().then(setAccounts);
      searchPaymentMethods({}).then(setPaymentMethods);
    }
  }, [configuration?.qboEnabled]);
  // @hipaa-exclude-end

  async function onClickDisconnect() {
    await disconnect();
    await setConnectionInfo(undefined);
    await refresh();
  }

  async function onClickConnect() {
    const authUrl = await getAuthUrl();
    window.location.href = authUrl;
  }

  async function onConfChange(qboConfiguration: QBOConfiguration) {
    const updated = await updateConfiguration(qboConfiguration);
    setConfigurations((existing) => [updated, ...(existing || []).filter((c) => c.name !== updated.name)]);
  }

  const isLoading =
    qboEnabled === undefined || (qboEnabled && (accounts === undefined || configurations === undefined));

  return (
    <div className="qbo-account container-v2">
      <div className="qbo-account-header">
        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            Manage - QuickBooks Online
          </Typography>
        </div>
        {!isLoading && qboEnabled && (
          <Button className="button button-primary" onClick={onClickDisconnect}>
            Disconnect from QuickBooks
          </Button>
        )}
      </div>

      <div className="bg-white rounded-[9px] p-[16px] mt-[24px] qbo-account-body">
        {!isLoading && (
          <>
            {qboEnabled ? (
              <>
                {connectionInfo && connectionInfo.issues.length > 0 && (
                  <div className="text-[var(--error-500)]">
                    There are issues with your QuickBooks Online configuration that must be resolved to enable invoice
                    syncing
                    <ul>
                      {connectionInfo.issues.map((issue) => (
                        <li>{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="input-group">
                  <label>
                    <Typography type="text-md" weight="semi-bold">
                      Company Name:
                    </Typography>
                  </label>
                  <Typography type="text-md">{connectionInfo?.companyInfo.CompanyName || '-'}</Typography>
                </div>
                <div className="input-group">
                  <label>
                    <Typography type="text-md" weight="semi-bold">
                      Connected At:
                    </Typography>
                  </label>
                  <Typography type="text-md">{connectionInfo?.connectedAt.toFormat('D t') || '-'}</Typography>
                </div>
                <div>
                  <QboItemIncomeAccount label="Default Income Account" accounts={accounts} />
                  <Typography type="text-xs" className="input-description">
                    The default income account that will be used for all Quipli products and services unless a more
                    specific configuration exists.
                  </Typography>
                </div>
                <div>
                  <QboItemIncomeAccount
                    transactionType={OrderType.Rental}
                    label="Default Rental Income Account"
                    accounts={accounts}
                  />
                  <Typography type="text-xs" className="input-description">
                    The income account that will be used for all rental line items. If blank default income account will
                    be used.
                  </Typography>
                </div>
                <div>
                  <QboItemIncomeAccount
                    transactionType={OrderType.Sale}
                    label="Default Sale Income Account"
                    accounts={accounts}
                  />
                  <Typography type="text-xs" className="input-description">
                    The income account that will be used for all sale line items. If blank default income account will
                    be used.
                  </Typography>
                </div>
                <div>
                  <QboItemIncomeAccount
                    itemType={QBOItemType.Freight}
                    label="Delivery Income Account"
                    accounts={accounts}
                  />
                  <Typography type="text-xs" className="input-description">
                    The income account that will be used for all delivery line items. If blank default income account
                    will be used.
                  </Typography>
                </div>
                <div>
                  <QboItemIncomeAccount
                    itemType={QBOItemType.UpchargePayment}
                    label="Up-Invoice Income Account"
                    accounts={accounts}
                  />
                  <Typography type="text-xs" className="input-description">
                    The income account that will be used for all up-charge line items. If blank default income account
                    will be used.
                  </Typography>
                </div>
                <div>
                  <QboItemIncomeAccount
                    itemType={QBOItemType.DamageWaiver}
                    label="Damage Waiver Income Account"
                    accounts={accounts}
                  />
                  <Typography type="text-xs" className="input-description">
                    The income account that will be used for all damage waiver line items. If blank default income
                    account will be used.
                  </Typography>
                </div>
                <div>
                  <QboItemIncomeAccount
                    itemType={QBOItemType.Coupon}
                    label="Coupon Income Account"
                    accounts={accounts}
                  />
                  <Typography type="text-xs" className="input-description">
                    The income account that will be used for all coupon line items. If blank default income account will
                    be used.
                  </Typography>
                </div>
                <div>
                  <QboItemIncomeAccount itemType={QBOItemType.Refund} label="Refund From Account" accounts={accounts} />
                  <Typography type="text-xs" className="input-description">
                    The income account that will be used for all refund line items. If blank default income account will
                    be used.
                  </Typography>
                </div>
                <div>
                  <div className="input-group">
                    <label>
                      <Typography type="text-md" weight="semi-bold">
                        Default Payment Account:
                      </Typography>
                    </label>
                    <select
                      title="Deposit To"
                      required={false}
                      value={configurations?.find((c) => c.name === 'CONF_DEPOSIT_ACCOUNT' && c.value !== '0')?.value}
                      onChange={(v) => onConfChange({ name: 'CONF_DEPOSIT_ACCOUNT', value: v.target.value })}
                    >
                      <option value={undefined}></option>
                      {accounts
                        ?.filter((a) => ['Bank', 'Other Current Asset'].indexOf(a.AccountType) >= 0)
                        .map((a) => (
                          <option key={a.Id} value={a.Id}>
                            {a.Name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <Typography type="text-xs" className="input-description">
                    Reference to the account to be used for payments. If you select "None", payment is applied to the
                    Undeposited Funds account.
                  </Typography>
                </div>
                {paymentMethods
                  ?.filter((pm) => pm.name !== 'overpayment' && pm.name !== 'on_account')
                  .map((paymentMethod) => (
                    <QboItemIncomeAccount
                      itemType={QBOItemType.PaymentMethod}
                      itemId={paymentMethod.id}
                      label={`${paymentMethod.displayName} Account`}
                      accounts={accounts?.filter((a) => ['Bank', 'Other Current Asset'].indexOf(a.AccountType) >= 0)}
                    />
                  ))}
              </>
            ) : (
              <div className="connect-wrapper">
                <ConnectToQboButton onClick={onClickConnect} />
              </div>
            )}
          </>
        )}
        {isLoading && <Spinner />}
      </div>
    </div>
  );
};
